import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { isInstalledPwa } from 'core/pwa/isInstalledPwa'
import { CHROME_RADIUS, SAFE_AREA_TOP, TOUCH_MIN } from 'core/theme.tokens'

const useOnline = () => {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  return online
}

export const OfflineBanner: React.FC = () => {
  const online = useOnline()
  if (online) return null
  return (
    <Alert severity='warning' sx={{ mx: 1, mt: 1, borderRadius: `${CHROME_RADIUS}px` }}>
      You are offline. Some portal actions will not save until you reconnect.
    </Alert>
  )
}

export const PwaUpdateBanner: React.FC = () => {
  const [needRefresh, setNeedRefresh] = useState(false)
  const [update, setUpdate] = useState<(() => void) | null>(null)

  useEffect(() => {
    if (!import.meta.env.PROD || !isInstalledPwa()) return
    let cancelled = false
    void import('virtual:pwa-register').then(({ registerSW }) => {
      if (cancelled) return
      let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | undefined
      applyUpdate = registerSW({
        immediate: true,
        onNeedRefresh() {
          if (!cancelled) setNeedRefresh(true)
        },
        onOfflineReady() {
          if (!cancelled) setNeedRefresh(false)
        },
      })
      setUpdate(() => () => {
        void applyUpdate?.(true)
      })
    })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Dialog
      open={needRefresh}
      aria-labelledby='pwa-update-title'
      maxWidth='xs'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${CHROME_RADIUS}px`,
          mx: 2,
        },
      }}
    >
      <DialogTitle id='pwa-update-title' sx={{ textAlign: 'center', pb: 1 }}>
        Update available
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
        <Typography color='text.secondary'>
          A newer portal version is ready. Reload to get the latest fixes and features.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3, gap: 1 }}>
        <Button
          variant='contained'
          onClick={() => update?.()}
          sx={{ minHeight: TOUCH_MIN, minWidth: 140 }}
        >
          Reload now
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const PwaChrome: React.FC = () => (
  <>
    <PwaUpdateBanner />
    <Box
      sx={{
        position: 'fixed',
        top: SAFE_AREA_TOP,
        left: 0,
        right: 0,
        zIndex: theme => theme.zIndex.snackbar,
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      <OfflineBanner />
    </Box>
  </>
)
